import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button } from 'reactstrap';
import RefreshIcon from 'mdi-react/RefreshIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import FormCard from '../../../shared/components/custom/FormCard';
import InfoBox from '../../../shared/components/custom/InfoBox';
import { partial } from '../../../util/translation/translation';
import {
  STATUS_COLORS,
  formatReceiverFees,
  receiverStatus,
  requirementLabels,
} from '../connect';

// One of the club's receivers: where onboarding is, what Stripe still needs,
// the fee (read-only; set by us) and the ways on to Stripe
const ConnectReceiverCard = ({
  receiver,
  onContinueOnboarding,
  onOpenDashboard,
  onRefresh,
}) => {
  const p = partial('connect');
  const status = receiverStatus(receiver);
  const disconnected = status === 'disconnected';
  const requirements = requirementLabels(receiver.requirementsDue);
  const canOnboard =
    !disconnected &&
    !!receiver.stripeAccountId &&
    (status === 'onboarding' || status === 'needsDetails');
  const fee = formatReceiverFees(receiver);

  return (
    <FormCard
      title={
        <>
          {receiver.name}
          {receiver.isClubDefault && (
            <Badge color="light" className="ml-2">
              {p('clubDefault')}
            </Badge>
          )}
        </>
      }
      actions={
        !disconnected &&
        receiver.stripeAccountId && (
          <Button
            type="button"
            outline
            size="sm"
            className="mb-0"
            onClick={onRefresh}
          >
            <RefreshIcon size={16} /> {p('refreshStatus')}
          </Button>
        )
      }
    >
      <dl className="settings-form__facts mb-3">
        <div>
          <dt>{p('status')}</dt>
          <dd>
            <Badge color={STATUS_COLORS[status]}>{p(`status-${status}`)}</Badge>
          </dd>
        </div>
        <div>
          <dt>{p('country')}</dt>
          <dd>
            {p(`country-${receiver.country}`)} ({receiver.currency})
          </dd>
        </div>
        <div>
          <dt>{p('fee')}</dt>
          <dd>
            {fee || p('feeNotSet')}
            {fee && (
              <div className="text-muted">{p('feeStripeIncludedShort')}</div>
            )}
          </dd>
        </div>
      </dl>
      <InfoBox
        className={canOnboard || receiver.detailsSubmitted ? '' : 'mb-0'}
      >
        {p(`statusText-${status}`)}
        {status === 'needsDetails' && (
          <>
            <br />
            <strong>
              {p('requirementsDue', {
                count: receiver.requirementsDue.length,
              })}
            </strong>
            <ul className="mb-0 mt-1 pl-3">
              {requirements.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </>
        )}
      </InfoBox>
      {(canOnboard || (receiver.detailsSubmitted && !disconnected)) && (
        <div className="form-card__actions">
          {canOnboard && (
            <Button
              type="button"
              color="primary"
              size="sm"
              onClick={onContinueOnboarding}
            >
              {receiver.detailsSubmitted
                ? p('addDetails')
                : p('continueOnboarding')}
              <ArrowRightIcon size={16} />
            </Button>
          )}
          {receiver.detailsSubmitted && !disconnected && (
            <Button type="button" outline size="sm" onClick={onOpenDashboard}>
              {p('openDashboard')} <OpenInNewIcon size={16} />
            </Button>
          )}
        </div>
      )}
    </FormCard>
  );
};

ConnectReceiverCard.propTypes = {
  receiver: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    country: PropTypes.string,
    currency: PropTypes.string,
    stripeAccountId: PropTypes.string,
    isClubDefault: PropTypes.bool,
    detailsSubmitted: PropTypes.bool,
    requirementsDue: PropTypes.arrayOf(PropTypes.string),
    feePercent: PropTypes.number,
    feeFixedAmount: PropTypes.number,
    productFees: PropTypes.objectOf(
      PropTypes.shape({
        feePercent: PropTypes.number,
        feeFixedAmount: PropTypes.number,
      })
    ),
  }).isRequired,
  onContinueOnboarding: PropTypes.func.isRequired,
  onOpenDashboard: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default ConnectReceiverCard;
